// apps/client/src/pages/leads/edm/EdmJourneysPage.tsx
//
// /leads/edm/journeys (plan JOURNEYS J8, `featureFlags.edm_journeys`): the
// company's automated journeys and a form to start a new one. Company-wide
// callers only (RequireEdmImportAccess on the route; the server re-checks).
// Reads only through edmJourneyAction; redirects to /leads/edm without the flag.

import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useOutletContext } from 'react-router-dom';
import { Plus, Route as RouteIcon } from 'lucide-react';
import { PageHeader } from '../../../components/common/PageHeader';
import { SkeletonTablePage } from '../../../components/common/SkeletonLoader';
import { useAuth } from '../../../hooks/useAuth';
import {
  isEdmJourneysEnabled,
  journeyErrorMessage,
  JOURNEY_STATUS_LABEL,
  triggerLabel,
  useEdmJourneys,
  type EdmJourneyProjection,
  type EdmJourneyTrigger,
} from '../../../hooks/useEdmJourneys';
import type { EdmAudience } from '../../../hooks/useEdmCampaigns';
import { EDM_UI, canUseClientsSource, formatBrisbaneDateTime } from '../../../utils/edm';

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
  companyData?: { featureFlags?: Record<string, unknown> | null } | null;
}

type TriggerType = EdmJourneyTrigger['type'];

const STATUS_CHIP: Record<string, string> = {
  draft: 'bg-gray-700/60 text-gray-200 border-gray-600',
  active: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/40',
  paused: 'bg-amber-500/15 text-amber-200 border-amber-500/40',
  archived: 'bg-gray-800 text-gray-400 border-gray-700',
};

/** The trigger the form describes, or a validation message. */
export function triggerFromForm(type: TriggerType, leadDays: string, tagNorm: string, offsetDays: string): EdmJourneyTrigger | string {
  if (type === 'settlement_anniversary') return { type };
  if (type === 'fixed_rate_expiry') {
    const n = Number(leadDays);
    if (!Number.isInteger(n) || n < 7 || n > 180) return 'Days before the fixed rate ends must be 7 to 180.';
    return { type, leadDays: n };
  }
  const tag = tagNorm.trim().toLowerCase();
  const n = Number(offsetDays);
  if (!tag) return 'Choose the tag whose event date starts this journey.';
  if (!Number.isInteger(n) || n < -60 || n > 30) return 'Days from the event date must be -60 to 30.';
  return { type, tagNorm: tag, offsetDays: n };
}

const EdmJourneysPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const { claims } = useAuth();
  const navigate = useNavigate();
  const companyId = ctx?.userData?.companyId;
  const role = ctx?.userData?.role ?? claims?.role;
  const enabled = isEdmJourneysEnabled(ctx?.companyData?.featureFlags);
  const api = useEdmJourneys(companyId);

  const [journeys, setJourneys] = useState<EdmJourneyProjection[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<TriggerType>('settlement_anniversary');
  const [leadDays, setLeadDays] = useState('60');
  const [tagNorm, setTagNorm] = useState('');
  const [offsetDays, setOffsetDays] = useState('0');
  const [audience, setAudience] = useState<EdmAudience>({ includeLeads: true, includeClients: false, includeContacts: false });

  const load = useCallback(() => {
    if (!enabled || !companyId) return;
    setLoading(true);
    setError(null);
    api
      .list()
      .then((res) => setJourneys(res.journeys))
      .catch((err) => setError(journeyErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [api, companyId, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  if (!enabled) return <Navigate to="/leads/edm" replace />;
  if (loading && journeys === null) return <SkeletonTablePage tableRows={4} tableColumns={4} />;

  const clientsAllowed = canUseClientsSource(role);
  // Fix pass X9 (Codex-10): a role that cannot email clients only has the
  // tag-event trigger, for what is shown AND what is submitted.
  const effectiveType: TriggerType = clientsAllowed ? type : 'tag_event';
  const clientOnly = effectiveType !== 'tag_event';

  const create = async () => {
    setFormError(null);
    const trigger = triggerFromForm(effectiveType, leadDays, tagNorm, offsetDays);
    if (typeof trigger === 'string') {
      setFormError(trigger);
      return;
    }
    if (!name.trim()) {
      setFormError('Give the journey a name.');
      return;
    }
    const aud: EdmAudience = clientOnly ? { includeLeads: false, includeClients: true, includeContacts: false } : audience;
    if (!aud.includeLeads && !aud.includeClients && !aud.includeContacts) {
      setFormError('Choose who this journey emails.');
      return;
    }
    setBusy(true);
    try {
      const { journeyId } = await api.create({ name: name.trim(), trigger, audience: aud });
      navigate(`/leads/edm/journeys/${journeyId}`);
    } catch (err) {
      setFormError(journeyErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={RouteIcon}
        title="Journeys"
        subtitle="Automated emails on a settlement anniversary, before a fixed rate ends, or around a tagged event date"
        onRefresh={load}
        isRefreshing={loading}
        actions={
          <button type="button" className={EDM_UI.primaryButton} onClick={() => setCreating((v) => !v)}>
            <Plus className="h-4 w-4" /> New journey
          </button>
        }
      />

      {error && <div className={EDM_UI.errorBanner}>{error}</div>}

      {creating && (
        <section className={EDM_UI.card} aria-label="New journey">
          <h2 className={EDM_UI.heading}>New journey</h2>
          <div>
            <label htmlFor="journey-name" className={EDM_UI.label}>Name</label>
            <input id="journey-name" className={EDM_UI.input} maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label htmlFor="journey-trigger" className={EDM_UI.label}>Starts when</label>
            <select id="journey-trigger" className={EDM_UI.input} value={effectiveType} onChange={(e) => setType(e.target.value as TriggerType)}>
              {clientsAllowed && <option value="settlement_anniversary">A client&apos;s settlement anniversary</option>}
              {clientsAllowed && <option value="fixed_rate_expiry">A client&apos;s fixed rate is about to end</option>}
              <option value="tag_event">A tagged event date</option>
            </select>
          </div>
          {effectiveType === 'fixed_rate_expiry' && (
            <div>
              <label htmlFor="journey-lead" className={EDM_UI.label}>Days before the fixed rate ends (7-180)</label>
              <input id="journey-lead" type="number" className={EDM_UI.input} value={leadDays} onChange={(e) => setLeadDays(e.target.value)} />
            </div>
          )}
          {effectiveType === 'tag_event' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="journey-tag" className={EDM_UI.label}>Tag (with an event date)</label>
                <input id="journey-tag" className={EDM_UI.input} maxLength={60} value={tagNorm} onChange={(e) => setTagNorm(e.target.value)} />
              </div>
              <div>
                <label htmlFor="journey-offset" className={EDM_UI.label}>Days from the event date (-60 to 30)</label>
                <input id="journey-offset" type="number" className={EDM_UI.input} value={offsetDays} onChange={(e) => setOffsetDays(e.target.value)} />
              </div>
            </div>
          )}
          {clientOnly ? (
            <p className="text-sm text-gray-400">This journey emails clients only.</p>
          ) : (
            <fieldset className="space-y-2">
              <legend className={EDM_UI.label}>Who it emails</legend>
              {(
                [
                  ['includeLeads', 'Leads', true],
                  ['includeClients', 'Clients', clientsAllowed],
                  ['includeContacts', 'Imported contacts', true],
                ] as const
              ).map(([key, label, allowed]) =>
                allowed ? (
                  <label key={key} className="flex items-center gap-2 text-sm text-gray-200">
                    <input type="checkbox" checked={audience[key] === true} onChange={(e) => setAudience((a) => ({ ...a, [key]: e.target.checked }))} />
                    {label}
                  </label>
                ) : null,
              )}
            </fieldset>
          )}
          {formError && <div className={EDM_UI.errorBanner}>{formError}</div>}
          <div className="flex gap-2">
            <button type="button" className={EDM_UI.primaryButton} disabled={busy} onClick={() => void create()}>
              Create journey
            </button>
            <button type="button" className={EDM_UI.secondaryButton} onClick={() => setCreating(false)}>
              Cancel
            </button>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-gray-700 bg-gray-800/40">
        {journeys && journeys.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400">No journeys yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700 text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="px-4 py-3">Journey</th>
                <th className="px-4 py-3">Starts when</th>
                <th className="px-4 py-3">Steps</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {(journeys ?? []).map((j) => (
                <tr
                  key={j.id}
                  className="cursor-pointer border-b border-gray-800 hover:bg-gray-800/60"
                  onClick={() => navigate(`/leads/edm/journeys/${j.id}`)}
                >
                  <td className="px-4 py-3 text-gray-100">{j.name}</td>
                  <td className="px-4 py-3 text-gray-300">{triggerLabel(j.trigger)}</td>
                  <td className="px-4 py-3 text-gray-300">{j.steps.length}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_CHIP[j.status] ?? STATUS_CHIP.draft}`}>{JOURNEY_STATUS_LABEL[j.status] ?? j.status}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-400">{j.updatedAt ? formatBrisbaneDateTime(j.updatedAt) : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default EdmJourneysPage;
